import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, clearAuth, errMsg, subjectMeta } from "../lib/api";
import { House, EnvelopeSimple, LockKey, Users, SignOut, ShieldCheck, GraduationCap, CheckCircle, Trophy, Fire, Plus, ChartLineUp, Target, CameraPlus, ArrowRight, ClockCounterClockwise, FileText, XCircle, MinusCircle, Key } from "@phosphor-icons/react";

const dateLabel = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function Parent() {
  const nav = useNavigate();
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [unlocks, setUnlocks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [showChildPwd, setShowChildPwd] = useState(false);
  const [childPwd, setChildPwd] = useState("");
  const [childPwdBusy, setChildPwdBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, h] = await Promise.all([
        api.get("/parent/summary"),
        api.get("/parent/unlocks"),
        api.get("/submissions"),
      ]);
      setSummary(s.data); setUnlocks(u.data); setSubmissions(h.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doUnlock = async (sid) => {
    try {
      await api.post(`/parent/unlock/${sid}`);
      toast.success("Çözümler açıldı — çocuğunuzun ekranında görünür.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addSecondary = async (e) => {
    e.preventDefault();
    try {
      await api.post("/parent/add-secondary", { email: newEmail, name: newName, password: newPwd });
      toast.success("İkinci veli eklendi.");
      setNewEmail(""); setNewName(""); setNewPwd("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const resetChildPassword = async (e) => {
    e.preventDefault();
    if (childPwd.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı."); return; }
    setChildPwdBusy(true);
    try {
      await api.post("/parent/student/reset-password", { new_password: childPwd });
      toast.success("Çocuğun yeni şifresi kaydedildi.");
      setChildPwd(""); setShowChildPwd(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setChildPwdBusy(false); }
  };

  const onLogout = () => { clearAuth(); nav("/login"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center font-nunito text-brand-ink2">Yükleniyor...</div>;
  const p = summary?.parent; const s = summary?.student; const w = summary?.weekly; const m = summary?.monthly;

  const StatPill = ({ label, value, tone = "ink", testid }) => (
    <div className={`rounded-2xl px-3 py-2 flex-1 min-w-0 ${tone === "primary" ? "bg-brand-primary/20" : "bg-white/10"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-nunito font-bold opacity-70">{label}</div>
      <div className="font-outfit font-extrabold text-lg leading-tight">{value}</div>
    </div>
  );

  return (
    <div className="w-full max-w-md mx-auto min-h-[100dvh] bg-brand-paper">
      <header className="sticky top-0 z-30 bg-brand-paper/90 backdrop-blur border-b border-black/5 px-5 py-4 flex items-center justify-between">
        <div>
          <div className="font-outfit font-extrabold text-lg text-brand-ink">Ödevix · Veli</div>
          {p && <div className="font-nunito text-xs text-brand-ink2 mt-1">{p.name} · {p.is_primary ? "Birincil" : "İkincil"}</div>}
        </div>
        <div className="flex gap-2 items-center">
          {p?.is_admin && <Link to="/admin" data-testid="parent-admin-link" className="p-2 rounded-full hover:bg-brand-paperAlt" title="Admin"><ShieldCheck size={20} weight="duotone" color="#FFA200" /></Link>}
          <button onClick={onLogout} data-testid="parent-logout" className="p-2 rounded-full hover:bg-brand-paperAlt"><SignOut size={20} weight="duotone" /></button>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-5 pt-4 flex gap-2 overflow-x-auto pb-2" data-testid="parent-tabs">
        {[
          {k:"summary", l:"Özet", icon:House},
          {k:"history", l:"Geçmiş", icon:ClockCounterClockwise},
          {k:"unlocks", l:`Onay bekleyen${summary?.pending_unlocks ? ` (${summary.pending_unlocks})` : ""}`, icon:LockKey},
          {k:"family", l:"Aile", icon:Users},
        ].map(({k,l,icon:I}) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`parent-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-nunito font-semibold text-sm whitespace-nowrap ${tab === k ? "bg-brand-ink text-white" : "bg-brand-paperAlt text-brand-ink2"}`}>
            <I size={16} weight={tab === k ? "fill" : "duotone"} /> {l}
          </button>
        ))}
      </div>

      <main className="px-5 py-4 pb-20">
        {tab === "summary" && (
          <>
            {s && (
              <div className="bg-brand-ink text-white rounded-3xl p-5 mb-5" data-testid="parent-student-card">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary flex items-center justify-center"><GraduationCap size={24} weight="duotone" color="#1D3557" /></div>
                  <div className="flex-1">
                    <div className="font-outfit font-extrabold text-xl">{s.child_name}</div>
                    <div className="font-nunito text-xs text-white/70">{s.grade} · kullanıcı: <b className="text-white">{s.username}</b></div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-white/10 rounded-2xl p-3 flex items-center gap-2"><Trophy size={18} weight="fill" color="#FFB703" /><span className="font-outfit font-bold">{s.xp} XP</span></div>
                  <div className="bg-white/10 rounded-2xl p-3 flex items-center gap-2"><Fire size={18} weight="fill" color="#E76F51" /><span className="font-outfit font-bold">{s.streak_days} gün</span></div>
                </div>
              </div>
            )}
            {s && (
              <button
                data-testid="parent-upload-cta"
                onClick={() => nav("/parent/upload")}
                className="w-full bg-brand-primary text-brand-ink rounded-3xl p-4 mb-5 flex items-center gap-3 shadow-[0_4px_18px_rgba(255,183,3,0.45)] active:scale-[0.99] transition-transform"
              >
                <div className="w-11 h-11 rounded-2xl bg-brand-ink flex items-center justify-center flex-shrink-0">
                  <CameraPlus size={22} weight="duotone" color="#FFB703" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-outfit font-extrabold text-base">Ödev fotoğrafı yükle</div>
                  <div className="font-nunito text-xs text-brand-ink/80">{s.child_name} adına yükle · aynı rapora işler</div>
                </div>
                <ArrowRight size={20} weight="bold" />
              </button>
            )}
            {w && (
              <section className="bg-white rounded-3xl p-5 shadow-sm mb-5" data-testid="parent-weekly">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-outfit font-extrabold text-brand-ink">Bu haftaki ödevler</div>
                  {w.accuracy_pct !== null && w.accuracy_pct !== undefined && (
                    <span data-testid="parent-weekly-accuracy" className="text-xs font-nunito font-bold px-2.5 py-1 rounded-full bg-brand-paperAlt text-brand-ink">%{w.accuracy_pct} doğru</span>
                  )}
                </div>
                {w.submissions?.length === 0 && <div className="font-nunito text-brand-ink2 text-sm">Bu hafta ödev yüklenmedi.</div>}
                <div className="flex flex-col gap-2">
                  {(w.submissions || []).map((sub) => {
                    const sm = subjectMeta(sub.subject);
                    return (
                      <div key={sub.id} className="flex items-center gap-3 bg-brand-paperAlt rounded-2xl p-3" data-testid={`parent-sub-${sub.id}`}>
                        <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: sm.bg }}><CheckCircle size={18} color={sm.color} weight="duotone" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="font-outfit font-bold text-sm text-brand-ink">{sm.name}</div>
                          <div className="text-xs font-nunito text-brand-ink2">{dateLabel(sub.created_at)}</div>
                        </div>
                        <div className="text-xs font-nunito font-bold">
                          <span className="text-good">{sub.correct}D</span> · <span className="text-bad">{sub.incorrect}Y</span> · <span className="text-blank">{sub.blank}B</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {w.weak_topics?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs uppercase tracking-widest font-nunito font-extrabold text-bad mb-2">Bu haftaki zayıf konular</div>
                    <div className="flex flex-wrap gap-2">{w.weak_topics.map((t,i) => <span key={i} className="px-3 py-1.5 rounded-full bg-bad-bg text-bad font-nunito font-semibold text-sm">{t}</span>)}</div>
                  </div>
                )}
              </section>
            )}

            {m && (
              <section className="bg-white rounded-3xl p-5 shadow-sm mb-5" data-testid="parent-monthly">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ChartLineUp size={20} weight="duotone" color="#1D3557" />
                    <div className="font-outfit font-extrabold text-brand-ink">Son 30 gün</div>
                  </div>
                  {m.accuracy_pct !== null && m.accuracy_pct !== undefined && (
                    <span data-testid="parent-monthly-accuracy" className="text-xs font-nunito font-bold px-2.5 py-1 rounded-full bg-brand-primary/25 text-brand-ink">%{m.accuracy_pct} doğru</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-brand-paperAlt rounded-2xl p-3 text-center" data-testid="monthly-subs">
                    <div className="font-outfit font-extrabold text-xl text-brand-ink">{m.submissions_count}</div>
                    <div className="text-[11px] font-nunito text-brand-ink2 uppercase tracking-wider">ödev</div>
                  </div>
                  <div className="bg-brand-paperAlt rounded-2xl p-3 text-center" data-testid="monthly-tests">
                    <div className="font-outfit font-extrabold text-xl text-brand-ink">{m.tests_count}</div>
                    <div className="text-[11px] font-nunito text-brand-ink2 uppercase tracking-wider">test</div>
                  </div>
                  <div className="bg-brand-paperAlt rounded-2xl p-3 text-center" data-testid="monthly-avg">
                    <div className="font-outfit font-extrabold text-xl text-brand-ink">{m.avg_test_score ?? "—"}</div>
                    <div className="text-[11px] font-nunito text-brand-ink2 uppercase tracking-wider">test ort.</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs font-nunito font-bold">
                  <div className="rounded-xl bg-good-bg text-good py-1.5 text-center" data-testid="monthly-correct">{m.correct} doğru</div>
                  <div className="rounded-xl bg-bad-bg text-bad py-1.5 text-center" data-testid="monthly-incorrect">{m.incorrect} yanlış</div>
                  <div className="rounded-xl bg-blank-bg text-blank py-1.5 text-center" data-testid="monthly-blank">{m.blank} boş</div>
                </div>

                {m.strong_subjects?.length > 0 && (
                  <div className="mt-4" data-testid="parent-strong">
                    <div className="text-xs uppercase tracking-widest font-nunito font-extrabold text-good mb-2 flex items-center gap-1.5">
                      <Target size={14} weight="fill" /> Güçlü dersler
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {m.strong_subjects.map((it, i) => {
                        const sm = subjectMeta(it.subject);
                        return (
                          <span key={i} className="px-3 py-1.5 rounded-full font-nunito font-semibold text-sm" style={{ background: sm.bg, color: sm.color }}>
                            {sm.name} · %{it.accuracy}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {tab === "history" && (
          <section data-testid="parent-history">
            <div className="font-outfit font-extrabold text-brand-ink text-lg mb-3">Ödev geçmişi</div>
            {submissions.length === 0 && (
              <div className="font-nunito text-brand-ink2 py-8 text-center">Henüz ödev yüklenmedi. <FileText size={18} className="inline" /></div>
            )}
            <div className="flex flex-col gap-3">
              {submissions.map((it) => {
                const sm = subjectMeta(it.subject);
                return (
                  <button
                    key={it.id}
                    onClick={() => nav(`/parent/result/${it.id}`)}
                    data-testid={`parent-history-${it.id}`}
                    className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
                  >
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: sm.bg }}>
                      <FileText size={22} weight="duotone" color={sm.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-outfit font-bold text-brand-ink">{sm.name}</div>
                      <div className="text-xs font-nunito text-brand-ink2">{dateLabel(it.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-1.5 font-nunito font-bold text-xs">
                      <span className="text-good flex items-center gap-0.5"><CheckCircle size={14} weight="fill" />{it.correct}</span>
                      <span className="text-bad flex items-center gap-0.5"><XCircle size={14} weight="fill" />{it.incorrect}</span>
                      <span className="text-blank flex items-center gap-0.5"><MinusCircle size={14} weight="fill" />{it.blank}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {tab === "unlocks" && (
          <section data-testid="parent-unlocks">
            <div className="font-outfit font-extrabold text-brand-ink text-lg mb-3">Onay bekleyen çözümler</div>
            {unlocks.length === 0 && <div className="font-nunito text-brand-ink2">Onay bekleyen ödev yok. 🎉</div>}
            <div className="flex flex-col gap-3">
              {unlocks.map((u) => {
                const sm = subjectMeta(u.subject);
                return (
                  <div key={u.id} className="bg-white rounded-2xl p-4 shadow-sm" data-testid={`parent-unlock-${u.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: sm.bg }}><LockKey size={18} color={sm.color} weight="duotone" /></div>
                      <div className="flex-1">
                        <div className="font-outfit font-bold text-brand-ink">{sm.name}</div>
                        <div className="text-xs font-nunito text-brand-ink2">{dateLabel(u.created_at)} · {u.incorrect} yanlış · {u.blank} boş</div>
                      </div>
                    </div>
                    {u.weak_topics?.length > 0 && (
                      <div className="text-xs font-nunito text-brand-ink2 mb-3">Konu: {u.weak_topics.slice(0,3).join(", ")}</div>
                    )}
                    <button onClick={() => doUnlock(u.id)} data-testid={`parent-unlock-btn-${u.id}`} className="w-full bg-brand-primary text-brand-ink font-outfit font-bold py-2.5 rounded-full text-sm">Çözümleri çocuğuma göster</button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {tab === "family" && (
          <section data-testid="parent-family">
            <div className="font-outfit font-extrabold text-brand-ink text-lg mb-3">Aile</div>
            <div className="bg-white rounded-3xl p-5 shadow-sm mb-5">
              <div className="font-outfit font-bold text-sm text-brand-ink2 mb-2">Veliler</div>
              <div className="flex items-center gap-2 mb-2">
                <EnvelopeSimple size={16} /> <span className="font-nunito text-sm text-brand-ink">{p?.email}</span>
                <span className={`ml-auto text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${p?.is_primary ? "bg-brand-primary text-brand-ink" : "bg-brand-paperAlt text-brand-ink2"}`}>
                  {p?.is_primary ? "Birincil" : "İkincil"}
                </span>
              </div>
              {(summary?.co_parents || []).map((cp) => (
                <div key={cp.id} className="flex items-center gap-2 mb-1" data-testid={`co-parent-${cp.email}`}>
                  <EnvelopeSimple size={16} /> <span className="font-nunito text-sm text-brand-ink">{cp.email}</span>
                  <span className={`ml-auto text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${cp.is_primary ? "bg-brand-primary text-brand-ink" : "bg-brand-paperAlt text-brand-ink2"}`}>
                    {cp.is_primary ? "Birincil" : "İkincil"}
                  </span>
                </div>
              ))}
            </div>

            {s && (
              <div className="bg-white rounded-3xl p-5 shadow-sm mb-5" data-testid="parent-child-creds">
                <div className="font-outfit font-bold text-sm text-brand-ink2 mb-3 flex items-center gap-2">
                  <GraduationCap size={16} /> Çocuk hesabı giriş bilgileri
                </div>
                <div className="flex items-center gap-2 mb-2 text-sm font-nunito">
                  <span className="text-brand-ink2">Kullanıcı adı:</span>
                  <code className="bg-brand-paperAlt px-2 py-1 rounded-lg font-bold text-brand-ink" data-testid="parent-child-username">{s.username}</code>
                  <button
                    onClick={() => { navigator.clipboard?.writeText(s.username); toast.success("Kullanıcı adı kopyalandı"); }}
                    className="ml-auto text-xs font-bold text-brand-ink underline underline-offset-2"
                    data-testid="copy-child-username"
                  >Kopyala</button>
                </div>
                <div className="text-[11px] font-nunito text-brand-ink2 mb-3">
                  Şifrelerinizi güvenlik için saklamıyoruz. Çocuğunuz unuttuysa aşağıdan yeni şifre belirleyebilirsiniz.
                </div>
                {!showChildPwd ? (
                  <button
                    onClick={() => setShowChildPwd(true)}
                    data-testid="open-child-pwd-form"
                    className="w-full bg-brand-primary text-brand-ink font-outfit font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-2"
                  >
                    <Key size={16} weight="fill" /> Çocuğun şifresini yeniden belirle
                  </button>
                ) : (
                  <form onSubmit={resetChildPassword} className="flex flex-col gap-2">
                    <input
                      required type="password" minLength={6}
                      placeholder="Yeni şifre (en az 6 karakter)"
                      value={childPwd} onChange={(e) => setChildPwd(e.target.value)}
                      data-testid="child-new-pwd"
                      className="bg-brand-paper border border-black/10 rounded-2xl px-3 py-2.5 font-nunito text-sm text-brand-ink"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button" onClick={() => { setShowChildPwd(false); setChildPwd(""); }}
                        className="flex-1 bg-brand-paperAlt text-brand-ink font-outfit font-semibold py-2.5 rounded-full text-sm"
                        data-testid="cancel-child-pwd"
                      >Vazgeç</button>
                      <button
                        type="submit" disabled={childPwdBusy}
                        className="flex-1 bg-brand-ink text-white font-outfit font-bold py-2.5 rounded-full text-sm disabled:opacity-60"
                        data-testid="save-child-pwd"
                      >{childPwdBusy ? "Kaydediliyor..." : "Yeni şifreyi kaydet"}</button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {p?.is_primary && (
              <div className="bg-white rounded-3xl p-5 shadow-sm">
                <div className="font-outfit font-bold text-sm text-brand-ink2 mb-3 flex items-center gap-2"><Plus size={16} /> İkinci veli ekle</div>
                <form onSubmit={addSecondary} className="flex flex-col gap-2">
                  <input required type="email" placeholder="E-posta" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="add-sec-email" className="bg-brand-paper border border-black/10 rounded-2xl px-3 py-2.5 font-nunito text-sm text-brand-ink" />
                  <input placeholder="Ad (opsiyonel)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="add-sec-name" className="bg-brand-paper border border-black/10 rounded-2xl px-3 py-2.5 font-nunito text-sm text-brand-ink" />
                  <input required type="password" minLength={6} placeholder="Şifre (6+ karakter, sonra ilettikçe alır)" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="add-sec-pwd" className="bg-brand-paper border border-black/10 rounded-2xl px-3 py-2.5 font-nunito text-sm text-brand-ink" />
                  <button type="submit" data-testid="add-sec-submit" className="bg-brand-ink text-white font-outfit font-bold py-2.5 rounded-full text-sm">Ekle</button>
                </form>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
